import { createSharedStore } from './createSharedStore'

export interface Grid {
  handle: string
  slug: string
  visibility: 'public' | 'private'
  version: number
  rows: number
  updated_at: string
  expires_at?: string
  // SEM-67: undefined or true = editable; false = locked.
  editable?: boolean
}

export interface Workspace {
  handle: string | null
  grids: Grid[]
}

export interface WsState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  workspace: Workspace | null
  keyCount: number | null
  plan: string | null
  storageBytes: number | null
  storageLimitBytes: number | null
  error: string
}

export const wsStore = createSharedStore<WsState>({
  status: 'idle',
  workspace: null,
  keyCount: null,
  plan: null,
  storageBytes: null,
  storageLimitBytes: null,
  error: '',
})

let wsInflight: Promise<void> | null = null

export function loadWorkspace() {
  if (wsInflight) return
  const cur = wsStore.get()
  if (cur.status === 'idle') wsStore.set({ ...cur, status: 'loading' })
  wsInflight = Promise.all([
    fetch('/api/workspace', { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
    fetch('/api/keys', { credentials: 'include' }).then(r => r.ok ? r.json() : { keys: [] }).catch(() => ({ keys: [] })),
    fetch('/api/billing/status', { credentials: 'include' }).then(r => r.ok ? r.json() : { plan: 'free' }).catch(() => ({ plan: 'free' })),
  ]).then(([ws, keys, billing]: [{ handle?: string | null; grids?: Grid[]; storageBytes?: number; storageLimitBytes?: number; error?: string }, { keys?: unknown[] }, { plan?: string }]) => {
    wsStore.set({
      status: 'ready',
      workspace: { handle: ws.handle ?? null, grids: ws.grids ?? [] },
      keyCount: keys.keys?.length ?? 0,
      plan: billing.plan ?? 'free',
      storageBytes: ws.storageBytes ?? null,
      storageLimitBytes: ws.storageLimitBytes ?? null,
      error: '',
    })
  }).catch((e: unknown) => {
    const prev = wsStore.get()
    wsStore.set({
      status: 'error',
      workspace: prev.workspace,
      keyCount: prev.keyCount,
      plan: prev.plan,
      storageBytes: prev.storageBytes,
      storageLimitBytes: prev.storageLimitBytes,
      error: e instanceof Error ? e.message : 'Failed to load',
    })
  }).finally(() => { wsInflight = null })
}

export function resetWorkspace() {
  wsStore.set({ status: 'idle', workspace: null, keyCount: null, plan: null, storageBytes: null, storageLimitBytes: null, error: '' })
}
